import React, { FC, useCallback, useEffect, useState } from 'react';

import { CloudProvider } from '@ariksa/abac/api';
import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { filter } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { INIT_PAGE_INFO } from 'services/utils/constants';
import { customTheme } from 'theme';
import { Optional } from 'types/utils';

import {
  renderTimeStackedCell,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import {
  EnvironmentIcon,
  HighRiskIcon,
  RoleIcon,
  ThumbsUpIcon,
  UserIcon,
} from 'components/Icons';
import { PageInfo } from 'components/Navigation';
import { ConfirmationModal, TableActionsMenu } from 'components/Overlay';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { ConfigureJitApproval } from 'containers/Jit/Components/Configuration/ConfigureJitApproval';
import { ConfigureJITApprovers } from 'containers/Jit/Components/Configuration/ConfigureJITApprovers';
import JitRoleConfig from 'containers/Jit/Setup/Components/Configuration/JitRoleConfig';
import { selectJitSetup } from 'containers/Jit/Setup/selectors';
import { actions } from 'containers/Jit/Setup/slice';
import { CloudAccountName } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  viewBy?: string;
}

export const JitAccounts: FC<Props> = props => {
  const { viewBy } = props;
  const dispatch = useDispatch();
  const jitAddApprover = useDisclosure();
  const jitRoleConfig = useDisclosure();
  const configureApproval = useDisclosure();
  const disableJit = useDisclosure();
  const { jitAccounts, abacAccounts, deleteJitApprover } = useSelector(
    selectJitSetup,
  );
  const [activeAccount, setActiveAccount] = useState<
    Optional<CloudAccountInfoGetResponse>
  >(null);
  const { accountEnvironmentMapping } = useEnvironmentOptions();

  const getJitAccounts = useCallback(
    (info: PageInfo = INIT_PAGE_INFO) => {
      dispatch(
        actions.getJitAccounts({
          q: {},
          page: info,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    getJitAccounts();
  }, [getJitAccounts]);

  useEffect(() => {
    dispatch(
      actions.getAbacAccounts({
        q: { page: 1, size: 100 },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (jitRoleConfig.isOpen && activeAccount) {
      dispatch(actions.getRoles({ q: { accountId: activeAccount.uuid } }));
    }
  }, [dispatch, activeAccount, jitRoleConfig.isOpen]);

  const handleDisableJit = useCallback(
    (row: CloudAccountInfoGetResponse) => {
      dispatch(
        actions.deleteJitAccount({
          q: {
            accountId: row.uuid ?? '',
          },
          onSuccess: () => {
            getJitAccounts();
            disableJit.onClose();
          },
        }),
      );
    },
    [dispatch, disableJit, getJitAccounts],
  );

  const columns: TableColumnProps<CloudAccountInfoGetResponse>[] = [
    {
      header: <Box pl={9}>Account</Box>,
      render: ({ row }) => (
        <WithResourceIcon resourceType={row.cloud_type.toLowerCase()}>
          <StackedCell
            upper={<CloudAccountName accountId={row.uuid} />}
            lower={row.cloud_account_id}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Environment',
      accessor: 'environment',
      render: ({ row }) => (
        <HStack>
          <Box boxSize={5} color={customTheme.colors.gray['250']}>
            <Center>
              <EnvironmentIcon />
            </Center>
          </Box>
          <Box>{accountEnvironmentMapping?.[row?.uuid]?.name}</Box>
        </HStack>
      ),
      align: 'left',
    },
    ...(viewBy === 'cloud'
      ? [
          {
            header: 'Approvers',
            render: ({ row }) => row.approvers_count ?? 0,
          },
          {
            header: 'Roles',
            render: ({ row }) => row.jit_roles?.length ?? 0,
          },
        ]
      : [
          {
            header: 'Data Groups',
            render: ({ row }) =>
              abacAccounts.data.find(account => account.account_id === row.uuid)
                ?.data_group_count ?? 0,
          },
          {
            header: 'Approvers',
            render: ({ row }) => row.approvers_count ?? 0,
          },
        ]),
    {
      header: 'Enabled',
      render: ({ row }) => {
        return renderTimeStackedCell({ value: row.created_date });
      },
      align: 'left',
    },
    {
      header: 'Last Update',
      render: ({ row }) => {
        return renderTimeStackedCell({ value: row.last_updated });
      },
      align: 'left',
    },
    {
      header: 'Actions',
      //ce: 'right',
      render: ({ row }) => (
        <Box onClick={e => e.stopPropagation()}>
          <TableActionsMenu
            menuItems={[
              ...(viewBy === 'cloud'
                ? [
                    {
                      label: 'Configure JIT',
                      icon: <UserIcon />,
                      style: {},
                      group: {
                        list: [
                          {
                            label: 'Approvers',
                            icon: <UserIcon />,
                            onClick: () => {
                              jitAddApprover.onOpen();
                              setActiveAccount(row);
                            },
                            itemStyles: { pl: 8 },
                          },
                          {
                            label: 'Allow JIT Roles',
                            icon: <RoleIcon />,
                            onClick: () => {
                              setActiveAccount(row);
                              jitRoleConfig.onOpen();
                            },
                            itemStyles: { pl: 8 },
                          },
                        ],
                      },
                    },
                  ]
                : [
                    {
                      label: 'Set Approvers',
                      icon: <UserIcon />,
                      onClick: () => {
                        jitAddApprover.onOpen();
                        setActiveAccount(row);
                      },
                    },
                  ]),
              {
                label: 'Configure Auto Approval',
                icon: <ThumbsUpIcon />,
                onClick: () => {
                  configureApproval.onOpen();
                  setActiveAccount(row);
                },
              },
              // {
              //   label: 'Disable JIT',
              //   icon: <HighRiskIcon color="red" />,
              //   onClick: () => {
              //     setActiveAccount(row);
              //     disableJit.onOpen();
              //   },
              // },
            ]}
            styles={{
              group: {
                pl: 2,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: customTheme.colors.gray['300'],
              },
            }}
          />

          {/*) : (
            <Center>
              <ActionButton
                label="Configure approval"
                icon={<ThumbsUpIcon />}
                onClick={() => {
                  configureApproval.onOpen();
                  setActiveAccount(row);
                }}
              />
              <DeleteActionButton
                onConfirm={() => {
                  handleDelete(row);
                }}
                name={'CONFIRM'}
                type="jit account"
              />
            </Center>
          )}*/}
        </Box>
      ),
    },
  ];

  return (
    <Stack spacing={5} h="full" w="full">
      <Box flex={1}>
        <Table
          columns={columns}
          data={
            viewBy === 'cloud'
              ? filter(
                  jitAccounts.data,
                  o => o.cloud_type !== CloudProvider.Snowflake,
                )
              : filter(
                  jitAccounts.data,
                  o => o.cloud_type === CloudProvider.Snowflake,
                )
          }
          isLoading={jitAccounts.isLoading || abacAccounts.isLoading}
          pagination={{
            totalCount: jitAccounts.page.totalCount,
            pageInfo: jitAccounts.page.info,
            onChange(info: PageInfo) {
              getJitAccounts(info);
            },
          }}
          onRowClick={row => {
            setActiveAccount(row);
            // metadataDrawer.onOpen();
          }}
        />
      </Box>

      {/*{jitAddAccount.isOpen && (
        <JitAddAccountModal
          isOpen={jitAddAccount.isOpen}
          onClose={jitAddAccount.onClose}
        />
      )}*/}
      {jitAddApprover.isOpen && activeAccount && (
        <ConfigureJITApprovers
          account={activeAccount}
          isOpen={jitAddApprover.isOpen}
          onClose={jitAddApprover.onClose}
          viewBy={viewBy}
        />
      )}
      {jitRoleConfig.isOpen && activeAccount && (
        <JitRoleConfig
          account={activeAccount}
          isOpen={jitRoleConfig.isOpen}
          onClose={jitRoleConfig.onClose}
        />
      )}
      {configureApproval.isOpen && activeAccount && (
        <ConfigureJitApproval
          isOpen={configureApproval.isOpen}
          onClose={configureApproval.onClose}
          account={activeAccount}
        />
      )}
      {disableJit.isOpen && activeAccount && (
        <ConfirmationModal
          name="CONFIRM"
          confirmationText={`Type CONFIRM to disable JIT for ${activeAccount.cloud_account_id}.`}
          isOpen={disableJit.isOpen}
          onClose={disableJit.onClose}
          onConfirm={() => handleDisableJit(activeAccount)}
          isLoading={deleteJitApprover.isLoading}
        />
      )}
    </Stack>
  );
};
